import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import Header from "../../components/Customer/Header";
import Footer from "../../components/Customer/Footer";

// EmailJS config — fill these in after setting up at emailjs.com
const EMAILJS_SERVICE_ID = "service_x9hsiqe";
const EMAILJS_TEMPLATE_ID = "template_fw4nx9u";
const EMAILJS_PUBLIC_KEY = "xl6o2FbDrP_sIatDz";

function AirportTransfersPage() {
  const [formData, setFormData] = useState({
    pickupDate: "",
    pickupTime: "12:00", // Set default time to 12:00 PM
    numberOfPeople: 1,
    numberOfBaggages: 1,
    airport: "melbourne",
    transferType: "pickup",
    pickupLocation: "",
    dropoffLocation: "",
    customerName: "",
    email: "",
    phone: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to get airport name from code
  const getAirportName = (airportCode) => {
    const airports = {
      melbourne: "Melbourne Airport (MEL)",
      avalon: "Avalon Airport (AVV)",
      essendon: "Essendon Airport (MEB)"
    };
    return airports[airportCode] || airportCode;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields based on transfer type
    if (formData.transferType === "pickup" && !formData.dropoffLocation.trim()) {
      alert("Please enter the dropoff location for airport pickup service.");
      return;
    }
    
    if (formData.transferType === "dropoff" && !formData.pickupLocation.trim()) {
      alert("Please enter the pickup location for airport dropoff service.");
      return;
    }
    
    // Prepare the data in the exact format the backend expects
    const submitData = {
      transfer_type: formData.transferType,
      airport: formData.airport,
      pickup_location: formData.transferType === "dropoff" ? formData.pickupLocation : getAirportName(formData.airport),
      dropoff_location: formData.transferType === "pickup" ? formData.dropoffLocation : getAirportName(formData.airport),
      date: formData.pickupDate,
      time: formData.pickupTime || "12:00", // Ensure time is never empty
      number_of_people: parseInt(formData.numberOfPeople),
      number_of_baggages: parseInt(formData.numberOfBaggages),
      customer_name: formData.customerName,
      email: formData.email,
      phone: formData.phone
    };

    const transferTypeLabel = submitData.transfer_type === "pickup" ? "Airport Pickup" : "Airport Dropoff";
    const templateParams = {
      name: submitData.customer_name,
      email: submitData.email,
      message:
        `Service: ${transferTypeLabel}\n` +
        `Airport: ${getAirportName(formData.airport)}\n` +
        `Pickup Location: ${submitData.pickup_location}\n` +
        `Dropoff Location: ${submitData.dropoff_location}\n` +
        `Date: ${submitData.date}\n` +
        `Time: ${submitData.time}\n` +
        `Passengers: ${submitData.number_of_people}\n` +
        `Baggages: ${submitData.number_of_baggages}\n` +
        `Phone: ${submitData.phone}`,
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      alert("Airport transfer request submitted successfully! We'll contact you to confirm details and pricing.");
      setFormData({
        pickupDate: "",
        pickupTime: "12:00",
        numberOfPeople: 1,
        numberOfBaggages: 1,
        airport: "melbourne",
        transferType: "pickup",
        pickupLocation: "",
        dropoffLocation: "",
        customerName: "",
        email: "",
        phone: ""
      });
    } catch (error) {
      console.error("EmailJS error:", error);
      alert("Failed to submit transfer request. Please try again or contact us directly.");
    }
  };

  const isPickup = formData.transferType === "pickup";
  const isDropoff = formData.transferType === "dropoff";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-amber-100 font-sans overflow-x-hidden">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-6">
            <img src="/images/araliya-tours-logo.png" alt="Araliya Tours" className="h-36 w-auto" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 font-poppins mb-4">
            Airport Transfer Booking
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Professional airport pickup and dropoff service with luggage handling, 
            flight monitoring, and meet & greet assistance.
          </p>
        </div>

        {/* Main Form */}
        <div className="rounded-3xl bg-white/80 backdrop-blur border border-white/40 shadow-2xl ring-1 ring-black/5 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-8 py-6">
            <h2 className="text-2xl font-semibold text-white">Request Your Transfer</h2>
            <p className="text-blue-100 mt-1">Fill in the details below and we'll get back to you within 2 hours</p>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-8">
            {/* Customer Information - MOVED TO TOP */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Customer Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Service Details */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Service Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Service Type
                  </label>
                  <select
                    name="transferType"
                    value={formData.transferType}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  >
                    <option value="pickup">✈️ Airport Pickup</option>
                    <option value="dropoff">🛬 Airport Dropoff</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Airport
                  </label>
                  <select
                    name="airport"
                    value={formData.airport}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  >
                    <option value="melbourne">🛫 Melbourne Airport (MEL)</option>
                    <option value="avalon">🛫 Avalon Airport (AVV)</option>
                    <option value="essendon">🛫 Essendon Airport (MEB)</option>
                  </select>
                </div>
              </div>

              {/* Conditional Location Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {isPickup && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Dropoff Location
                    </label>
                    <input
                      type="text"
                      name="dropoffLocation"
                      value={formData.dropoffLocation}
                      onChange={handleInputChange}
                      placeholder="e.g., 123 Collins St, Melbourne"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                      required
                    />
                  </div>
                )}

                {isDropoff && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Pickup Location
                    </label>
                    <input
                      type="text"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleInputChange}
                      placeholder="e.g., 123 Collins St, Melbourne"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                      required
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Date & Time */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Schedule
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Date
                  </label>
                  <input
                    type="date"
                    name="pickupDate"
                    value={formData.pickupDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Time
                  </label>
                  <input
                    type="time"
                    name="pickupTime"
                    value={formData.pickupTime}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Passenger & Luggage */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Passengers & Luggage
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Number of People
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="numberOfPeople"
                      value={formData.numberOfPeople}
                      onChange={handleInputChange}
                      min="1"
                      max="12"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Number of Baggages
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="numberOfBaggages"
                      value={formData.numberOfBaggages}
                      onChange={handleInputChange}
                      min="0"
                      max="20"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Section */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-4 text-white font-semibold text-lg shadow-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600/40 transform hover:scale-[1.02] transition-all duration-200"
              >
                <span className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Request Airport Transfer
                </span>
              </button>
              
              <div className="mt-4 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-full">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  We'll contact you within 2 hours to confirm details and pricing
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default AirportTransfersPage;
